import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable} from '@tanstack/react-table';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import OeSelect from './OeSelect';
import Swatch from './Swatch';
import AvailIcon, {AvailGlyph} from './AvailIcon';
import WarehouseSlider from './WarehouseSlider';
import StockFigures from './StockFigures';
import {AutoReserveChip} from './AutoReserve';
import AddPositionRow from './AddPositionRow';
import BulkBar from './BulkBar';
import {fmt, fmt0, fmtWeDate, qtyNum} from './format';
import {isKitChild, isKitParent} from './rows';
import {isDirty} from './changes';
import {
    STAMM_LABELS,
    STAMM_NONE,
    availCat,
    freeOf,
    groupLabelOf,
    isExcluded,
    isStockInPflege,
    sourceWarehouseFor,
    stammLagerOf,
} from './availability';
import {activeFilterChips, compareValues, filterCat, filterEntries, sortValue} from './tableView';
import {CheckAllLahrButton, CheckButton, MassActions, StockActionButtons} from './StockActions';
import {WH_LAHR, checkKeyOf} from './wizard';

// Positionstabelle des Auftrags-Editors (Original `renderHead` 1856 und
// `renderRows` 4935). Clientseitig: gefiltert wird mit der Logik aus
// `tableView.js`, sortiert mit TanStack Table. Beide Kopfzeilen bleiben beim
// Scrollen oben stehen; die Suchzeile sitzt um die gemessene Höhe der
// Titelzeile tiefer (CSS-Variable `--th1h`).
//
// Ab Stufe 1 wird hier bearbeitet: Menge, Rabatt, Lagerwahl je Position,
// Löschmarkierung und neue Positionen. Geändertes ist blau markiert und geht
// erst mit „Speichern" nach weclapp — bis dahin bleibt der Beleg dort
// unberührt.

const columnHelper = createColumnHelper();

/** Masterbox-Kennzeichen am Artikelnamen (Original `masterboxBadge`, 1246). */
function MasterboxBadge({row, stock, mode}) {
    if (mode !== 'order' || row._new) {
        return null;
    }
    const mb = stock.masterbox[String(row.articleId)];
    if (!(mb > 0)) {
        return null;
    }

    const qty = qtyNum(row.quantity);
    const full = Math.floor(qty / mb);
    const rem = qty % mb;

    if (qty > 0 && rem === 0) {
        return (
            <OeTip tip={`Masterboxgröße ${fmt0(mb)} — genau ${fmt0(full)} volle Box${full === 1 ? '' : 'en'}`}>
                <span className="mbbadge full">📦 {fmt0(full)}× MB{fmt0(mb)} ✓</span>
            </OeTip>
        );
    }

    const toNext = mb - rem;
    const pctMiss = Math.round(toNext / mb * 100);

    return (
        <OeTip tip={`Masterboxgröße ${fmt0(mb)}: ${fmt0(full)} volle Box${full === 1 ? '' : 'en'} + ${fmt0(rem)} Rest. `
            + `Für die nächste volle Box fehlen ${fmt0(toNext)} Stück.`}>
            <span className="mbbadge">📦 MB{fmt0(mb)} · noch <b>{fmt0(toNext)}</b> ({pctMiss}%) bis volle Box</span>
        </OeTip>
    );
}

/** Staffel-Hinweis unter dem Rabattfeld (Original `staffelHint`, 4921). */
function StaffelHint({row, stock, mode, planByGroup}) {
    if (mode !== 'order' || !stock.groups || row._new) {
        return null;
    }
    const g = (stock.groups && stock.groups[String(row.articleId)]) || 'none';
    if (g === 'none') {
        return null;
    }
    const p = planByGroup[g];
    if (!p) {
        return null;
    }
    if (!p.hasTiers) {
        return (
            <OeTip tip="Für diese Rabattgruppe ist keine Staffel hinterlegt.">
                <span className="poinfo muted">keine Staffel</span>
            </OeTip>
        );
    }
    if (p.pct === null) {
        return (
            <OeTip tip={`Gruppen-Warenwert ${fmt(p.sum)} € — Stufe noch nicht erreicht`}>
                <span className="poinfo muted">Staffel ab {fmt0(p.next ? p.next[0] : 0)} €</span>
            </OeTip>
        );
    }
    const cur = parseFloat(String(row.discountPercentage).replace(',', '.')) || 0;
    return cur === p.pct
        ? <span className="poinfo" style={{color: 'var(--ok)'}}>Staffel {p.pct} % ✓</span>
        : (
            <OeTip tip="Die Staffel ist erreicht, der Rabatt an dieser Position aber noch nicht gesetzt.">
                <span className="poinfo" style={{color: 'var(--warn)'}}>Staffel {p.pct} % — offen</span>
            </OeTip>
        );
}

/** Einkaufsinfo unter der Ampel: offene Bestellung und geplanter Wareneingang. */
function PurchaseInfo({row, stock, order}) {
    if (row._new || !stock.loaded) {
        return null;
    }
    const v = freeOf(row, stock, order);
    if (v === undefined) {
        return null;
    }
    const need = parseFloat(String(row.quantity).replace(',', '.')) || 0;
    if (v >= need) {
        return null;
    }

    const p = stock.purchase[String(row.articleId)];
    if (p && p.qty > 0) {
        const dat = fmtWeDate(p.date);
        return (
            <OeTip tip={`offene Bestellung ${(p.pos || []).join(', ')} · ${fmt0(p.qty)} Stück offen · geplanter Wareneingang ${dat}`}>
                <span className="poinfo">📦 +{fmt0(p.qty)} · WE {dat}</span>
            </OeTip>
        );
    }

    return (
        <OeTip tip="Zu diesem Artikel liegt keine offene Einkaufsbestellung vor.">
            <span className="poinfo muted">nicht bestellt</span>
        </OeTip>
    );
}

/** Kennzeichnung „in Pflege" am Lahr-Bestand. */
function PflegeMarker() {
    return (
        <OeTip tip="Der Lahr-Bestand wird in weclapp noch aufgebaut — die Werte können Platzhalter sein. Sobald die Pflege abgeschlossen ist, entfällt dieses Kennzeichen.">
            <span className="oe-pflege" role="img" aria-label="Bestand in Pflege">⚠</span>
        </OeTip>
    );
}

/**
 * Preis-Vorschau einer neu hinzugefügten Position (Original
 * `previewPriceCell`, 5098). weclapp bepreist die Zeile erst beim Speichern,
 * deshalb „≈" — gespeichert wird der Vorschauwert nicht.
 */
function PreviewPrice({row}) {
    if (row._priceLoading) {
        return <span className="muted">…</span>;
    }
    if (row._previewPrice != null) {
        return (
            <OeTip tip="Vorschau aus dem Vertriebsweg-Preis — weclapp bestätigt den Preis beim Speichern.">
                <span className="muted">≈ {fmt(row._previewPrice)}</span>
            </OeTip>
        );
    }
    return (
        <OeTip tip="weclapp bepreist die Position beim Speichern.">
            <span className="muted">auto</span>
        </OeTip>
    );
}

/** Spaltenkopf mit Sortierpfeil und Erklärung. */
function HeadCell({label, sortKey, view, onSort, help, helpDetail, extraClass, width}) {
    const active = view.sort.key === sortKey;
    const sortable = !!sortKey;

    return (
        <th
            className={`${sortable ? 'sortable ' : ''}${extraClass || ''}`.trim()}
            style={width ? {width} : undefined}
            onClick={sortable ? () => onSort(sortKey) : undefined}
            aria-sort={active ? (view.sort.dir === 1 ? 'ascending' : 'descending') : undefined}
        >
            {label}
            {active ? <span className="arr">{view.sort.dir === 1 ? ' ▲' : ' ▼'}</span> : null}
            <span onClick={(e) => e.stopPropagation()}>
                <HelpTooltip id={`oe-col-${sortKey || label}`} placement="bottom" size={0.7} detail={helpDetail}>{help}</HelpTooltip>
            </span>
        </th>
    );
}

export default function PositionsTable({
    mode,
    order,
    rows,
    posLabelList,
    kitParents,
    stock,
    view,
    planByGroup,
    dispatch,
    onExport,
    exporting,
    canEdit,
    onSetAllStamm,
    onResetStamm,
    onAddPosition,
    onToast,
    step = 1,
    checked = [],
    pendingWarehouse = null,
    lahrGesperrt = false,
    canWarehouse = true,
    onBook,
    onUnpick,
    onPosOut,
    onToggleCheck,
    onMass,
    onCheckAllLahr,
    orderRef = null,
    autoReserve = null,
}) {
    const gridRef = useRef(null);
    const headRowRef = useRef(null);
    // Schritt 6 ist eine reine Pick-/Bestandsansicht: keine Preise und
    // Rabattgruppen (die macht der Rabatt-Schritt), „Liefern ab" nur noch als
    // Kennzeichen, dafür die Bestandsaktionen und die Spalte „Geprüft"
    // (Original `renderHead`, 1866).
    const kommStep = mode === 'order' && step === 6;
    const hasPrice = mode === 'order' && !kommStep;
    const showGroup = mode === 'order' && !kommStep;
    const showStamm = mode === 'order';

    // Bearbeitbar ist die Tabelle nur mit Recht und an einem Beleg, den auch
    // weclapp noch freigibt.
    const editable = !!(canEdit && order && order.editable);

    const [addStatus, setAddStatus] = useState(null);

    // Mehrfachauswahl (Ticket #124): Schlüssel der gewählten Zeilen. Im
    // Kommissionierungs-Schritt und ohne Bearbeitungsrecht gibt es sie nicht.
    const selectable = mode === 'order' && editable && !kommStep;
    const [sel, setSel] = useState(() => new Set());
    const lastSelRef = useRef(null);
    const rowKeyOf = (row, index) => String(row.id ?? row._key ?? `n${index}`);
    const warnedRef = useRef(new Set());

    // Stand des automatischen Reservierens je Position (Chip unter der Ampel).
    const autoPositionen = useMemo(() => {
        const out = {};
        const liste = (autoReserve && autoReserve.stand && autoReserve.stand.ergebnis
            && autoReserve.stand.ergebnis.positionen) || [];
        for (const p of liste) {
            out[String(p.itemId)] = p;
        }
        return out;
    }, [autoReserve && autoReserve.stand]);
    const autoLaeuft = !!(autoReserve && autoReserve.laeuft);

    // Arbeitsliste mit Belegreihenfolge und Positionsnummer.
    const entries = useMemo(
        () => rows.map((row, index) => ({row, index, pos: posLabelList[index]})),
        [rows, posLabelList],
    );

    const visible = useMemo(
        () => filterEntries(entries, view, stock, order, mode),
        [entries, view, stock, order, mode],
    );

    // Zähler der Verfügbarkeits-Chips zählen über ALLE Positionen, nicht nur
    // über die sichtbaren — sonst führten sie beim Filtern in die Irre.
    const availCounts = useMemo(() => {
        const c = {0: 0, 1: 0, 2: 0, 3: 0, none: 0};
        for (const r of rows) {
            const k = filterCat(r, stock, order);
            if (c[k] !== undefined) {
                c[k]++;
            }
        }
        return c;
    }, [rows, stock, order]);

    const onSort = useCallback((key) => dispatch({type: 'set-sort', key}), [dispatch]);
    const setView = useCallback((patch) => dispatch({type: 'set-view', patch}), [dispatch]);

    // Sortierbare Spalten brauchen eine Wertfunktion — ohne sie überspringt
    // TanStack die Spalte beim Sortieren. Der Vergleich ist derselbe wie im
    // Original (kleiner/größer ohne Sprachregeln, gleiche Werte behalten die
    // Reihenfolge des Belegs).
    const sortColumn = useCallback(
        (key, extra) => columnHelper.accessor((e) => sortValue(key, e, stock, order), {
            id: key,
            sortingFn: (a, b, columnId) => compareValues(a.getValue(columnId), b.getValue(columnId)),
            ...extra,
        }),
        [stock, order],
    );

    const columns = useMemo(() => {
        const cols = [
            columnHelper.display({id: 'pos'}),
            sortColumn('art'),
            sortColumn('name'),
        ];
        if (showGroup) {
            cols.push(sortColumn('group'));
        }
        if (showStamm) {
            cols.push(sortColumn('stamm'));
        }
        cols.push(sortColumn('avail'));
        cols.push(sortColumn('qty'));
        if (hasPrice) {
            cols.push(sortColumn('price'));
            cols.push(columnHelper.display({id: 'disc'}));
        }
        if (kommStep) {
            cols.push(columnHelper.display({id: 'check'}));
        }
        return cols;
    }, [showGroup, showStamm, hasPrice, kommStep, sortColumn]);

    const sorting = useMemo(
        () => (view.sort.key ? [{id: view.sort.key, desc: view.sort.dir === -1}] : []),
        [view.sort],
    );

    const table = useReactTable({
        data: visible,
        columns,
        state: {sorting},
        enableSortingRemoval: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: (e) => String(e.row.id ?? e.row._key ?? `n${e.index}`),
    });

    const sortedEntries = table.getRowModel().rows.map((r) => r.original);

    // Set-Köpfe und gesperrte Zeilen (Set-Inhalte, nicht mehr änderbare
    // Positionen) sind von der Mehrfachauswahl ausgenommen.
    const canSelectRow = (row) => !!row.articleId && !row.locked && !isKitParent(row, kitParents);
    const selEntries = selectable
        ? entries.filter((e) => sel.has(rowKeyOf(e.row, e.index)) && canSelectRow(e.row))
        : [];
    const visibleSelectable = sortedEntries.filter((e) => canSelectRow(e.row));
    const allVisibleSelected = visibleSelectable.length > 0
        && visibleSelectable.every((e) => sel.has(rowKeyOf(e.row, e.index)));

    const toggleSel = (entry, shift) => {
        const key = rowKeyOf(entry.row, entry.index);
        setSel((prev) => {
            const next = new Set(prev);
            const on = !prev.has(key);
            // Shift-Klick: alle sichtbaren Zeilen zwischen dem letzten Klick
            // und diesem bekommen denselben Zustand.
            const from = lastSelRef.current != null
                ? sortedEntries.findIndex((e) => rowKeyOf(e.row, e.index) === lastSelRef.current)
                : -1;
            const to = sortedEntries.findIndex((e) => rowKeyOf(e.row, e.index) === key);
            const bereich = shift && from >= 0 && to >= 0
                ? sortedEntries.slice(Math.min(from, to), Math.max(from, to) + 1)
                : [entry];
            for (const e of bereich) {
                if (!canSelectRow(e.row)) {
                    continue;
                }
                const k = rowKeyOf(e.row, e.index);
                if (on) {
                    next.add(k);
                } else {
                    next.delete(k);
                }
            }
            return next;
        });
        lastSelRef.current = key;
    };

    const toggleAllVisible = () => {
        setSel((prev) => {
            const next = new Set(prev);
            for (const e of visibleSelectable) {
                const k = rowKeyOf(e.row, e.index);
                if (allVisibleSelected) {
                    next.delete(k);
                } else {
                    next.add(k);
                }
            }
            return next;
        });
    };

    const BULK_LABEL = {
        stamm: (v) => `ab ${STAMM_LABELS[v] || v}`,
        discountPercentage: (v) => `Rabatt ${String(v).replace('.', ',')} %`,
        quantity: (v) => `Menge ${String(v).replace('.', ',')}`,
        del: () => 'zum Löschen markiert',
    };

    const applyBulk = (field, value) => {
        const indices = selEntries.map((e) => e.index);
        if (!indices.length) {
            return;
        }
        dispatch({type: 'bulk-patch', field, value, indices});
        onToast(`${indices.length} Position(en): ${BULK_LABEL[field](value)} — jetzt speichern`);
    };

    // Höhe der Titelzeile messen, damit die Suchzeile beim Scrollen exakt
    // darunter klebt. Zeilen liefern keine Höhe, deshalb über die erste Zelle.
    useLayoutEffect(() => {
        const messen = () => {
            const cell = headRowRef.current?.querySelector('th');
            const h = cell ? Math.round(cell.getBoundingClientRect().height) : 0;
            if (h && gridRef.current) {
                gridRef.current.style.setProperty('--th1h', h + 'px');
            }
        };
        messen();
        if (typeof ResizeObserver === 'undefined' || !headRowRef.current) {
            return undefined;
        }
        const ro = new ResizeObserver(messen);
        ro.observe(headRowRef.current);
        return () => ro.disconnect();
    }, [columns.length, view.sort]);

    // Häufigste Verwirrung im Betrieb: ein aktiver Filter blendet die gerade
    // hinzugefügte Position aus — der Bearbeiter hält sie für verloren
    // (Original `warnIfHidden`, 5086). Deshalb Statuszeile und Toast.
    const visibleIndices = useMemo(() => new Set(visible.map((e) => e.index)), [visible]);
    useEffect(() => {
        rows.forEach((r, i) => {
            if (!r._new) {
                return;
            }
            const key = r._key || `i${i}`;
            if (visibleIndices.has(i)) {
                warnedRef.current.delete(key);
                return;
            }
            if (warnedRef.current.has(key)) {
                return;
            }
            warnedRef.current.add(key);
            setAddStatus({tone: 'warn', text: `${r.articleNumber} hinzugefügt, aber durch aktiven Filter ausgeblendet`});
            if (onToast) {
                onToast(`⚠ ${r.articleNumber} ist durch einen Filter ausgeblendet — „Filter zurücksetzen" zeigt ihn`);
            }
        });
    }, [rows, visibleIndices, onToast]);

    // Rabattgruppen-Auswahl nur mit den Gruppen, die im Beleg vorkommen.
    const groupOptions = useMemo(() => {
        const present = new Set(rows.filter((r) => !r.del && r.articleId)
            .map((r) => (stock.groups && stock.groups[String(r.articleId)]) || 'none'));
        const opts = [{value: 'all', label: 'alle'}];
        for (const g of (stock.rules || [])) {
            if (present.has(g.key) || view.groupFilter === g.key) {
                opts.push({value: g.key, label: g.label});
            }
        }
        if (present.has('none')) {
            opts.push({value: 'none', label: 'ohne'});
        }
        return opts;
    }, [rows, stock.groups, stock.rules, view.groupFilter]);

    // Stammlager-Auswahl: Cosmos und „Keine" nur, wenn im Beleg vorhanden.
    const stammOptions = useMemo(() => {
        const present = new Set(rows.filter((r) => !r.del && r.articleId).map((r) => stammLagerOf(r, stock)));
        return [['all', 'alle'], ['Lahr', 'Lahr'], ['SNV', 'SNV'], ['Cosmos', 'Cosmos'], [STAMM_NONE, 'Keine']]
            .filter(([v]) => v === 'all' || v === 'Lahr' || v === 'SNV' || present.has(v) || v === view.stammFilter)
            .map(([value, label]) => ({value, label}));
    }, [rows, stock, view.stammFilter]);

    const chips = activeFilterChips(view, mode);
    const filterActive = chips.length > 0;
    let hiddenNew = 0;
    let hiddenTotal = 0;
    rows.forEach((r, i) => {
        if (!r.del && !visibleIndices.has(i)) {
            hiddenTotal++;
            if (r._new) {
                hiddenNew++;
            }
        }
    });

    const zeigeMasterSchalter = showStamm && editable && rows.some((r) => !r.del && r.articleId);

    // Massenaktionen und Sammelhaken wirken auf die gerade SICHTBAREN
    // Lahr-Positionen (Original `visibleLahrIdx` 3679 und
    // `checkableVisibleLahrIdx` 3650). Schneider und Cosmos bleiben draußen:
    // dort wird weder gebucht noch abgehakt.
    const massIndices = useMemo(() => (kommStep ? visible : [])
        .filter((e) => {
            const r = e.row;
            return r && !r.del && !r._new && r.articleId && !isKitParent(r, kitParents)
                && sourceWarehouseFor(r, stock, order) === WH_LAHR
                && availCat(r, stock, order) !== 3;
        })
        .map((e) => e.index), [kommStep, visible, kitParents, stock, order]);

    const checkIndices = useMemo(() => (kommStep ? visible : [])
        .filter((e) => {
            const r = e.row;
            return r && !r.locked && !r._new && !r.del && r.articleId && stammLagerOf(r, stock) === 'Lahr';
        })
        .map((e) => e.index), [kommStep, visible, stock]);

    const availChip = (cat, label, tip) => (
        <OeTip key={cat} tip={tip || `Nur Positionen mit dem Zustand „${label}" zeigen. Mehrere Zustände lassen sich gleichzeitig wählen.`}>
            <button
                type="button"
                className={`avfilt${view.availFilter.includes(cat) ? ' on' : ''}`}
                aria-pressed={view.availFilter.includes(cat)}
                aria-label={label}
                onClick={() => dispatch({type: 'toggle-avail', cat})}
            >
                <AvailGlyph cat={cat}/>
                <span className="avn">{availCounts[cat]}</span>
            </button>
        </OeTip>
    );

    const rangeFilter = (label, minKey, maxKey, help) => (
        <div className="rngf">
            <OeTip tip={`Untere Grenze für ${help}.`}>
                <input
                    className="rngi"
                    placeholder="von"
                    aria-label={`${label} von`}
                    value={view[minKey]}
                    inputMode="decimal"
                    autoComplete="off"
                    onChange={(e) => setView({[minKey]: e.target.value.trim()})}
                />
            </OeTip>
            <OeTip tip={`Obere Grenze für ${help}.`}>
                <input
                    className="rngi"
                    placeholder="bis"
                    aria-label={`${label} bis`}
                    value={view[maxKey]}
                    inputMode="decimal"
                    autoComplete="off"
                    onChange={(e) => setView({[maxKey]: e.target.value.trim()})}
                />
            </OeTip>
        </div>
    );

    return (
        <>
            <div className="viewbar" style={{display: 'flex'}}>
                {filterActive ? (
                    <OeTip
                        placement="bottom"
                        tip={hiddenNew
                            ? `${hiddenNew} neu hinzugefügte Position(en) sind ausgeblendet — „Filter zurücksetzen" macht sie sichtbar. Nichts ist gelöscht.`
                            : 'Ausgeblendete Positionen fehlen nur in der Ansicht (nicht gelöscht) — „Filter zurücksetzen" zeigt alle.'}
                    >
                        <span className={`filterbanner${hiddenNew ? ' alert' : ''}`}>
                            <span className="fb-lead">🔎 Filter</span> <b>{visible.length}/{rows.length}</b>
                            {hiddenNew
                                ? <> · <span className="fwarn">⚠ {hiddenNew} neu ausgeblendet</span></>
                                : (hiddenTotal ? <> · <span className="fwarn">{hiddenTotal} ausgeblendet</span></> : null)}
                            <span className="fchips">
                                {chips.map((c) => <span className="fchip" key={c}>{c}</span>)}
                            </span>
                        </span>
                    </OeTip>
                ) : <span className="muted"/>}

                {filterActive ? (
                    <OeTip tip="Setzt alle Filter der Kopfzeile zurück und zeigt wieder jede Position des Belegs. An den Daten ändert sich nichts.">
                        <button type="button" className="fbtn" onClick={() => dispatch({type: 'reset-view'})}>
                            ✕ Filter zurücksetzen
                        </button>
                    </OeTip>
                ) : null}

                <OeTip tip="Erzeugt eine Excel-Datei mit genau der Ansicht, die gerade zu sehen ist — dieselben Filter, dieselbe Sortierung. Die Datei wird heruntergeladen, in weclapp ändert sich nichts.">
                    <button
                        type="button"
                        className="fbtn"
                        style={{marginLeft: 'auto'}}
                        disabled={exporting}
                        onClick={() => onExport(sortedEntries)}
                    >
                        {exporting ? '⬇ Export läuft…' : '⬇ Excel-Export'}
                    </button>
                </OeTip>
            </div>

            <table id="oe-grid" ref={gridRef} style={{display: 'table'}}>
                <thead>
                    <tr id="oe-theadRow" ref={headRowRef}>
                        {selectable ? (
                            <th className="selcell">
                                <OeTip tip={allVisibleSelected
                                    ? 'Hebt die Auswahl der sichtbaren Positionen auf.'
                                    : `Wählt alle ${visibleSelectable.length} sichtbaren Positionen aus (Set-Köpfe und gesperrte ausgenommen). Shift-Klick an einer Zeile wählt einen Bereich.`}
                                >
                                    <button
                                        type="button"
                                        role="checkbox"
                                        aria-checked={allVisibleSelected}
                                        aria-label="Alle sichtbaren Positionen auswählen"
                                        className={`selbox${allVisibleSelected ? ' on' : ''}`}
                                        disabled={!visibleSelectable.length}
                                        onClick={toggleAllVisible}
                                    >
                                        {allVisibleSelected ? '✓' : ''}
                                    </button>
                                </OeTip>
                            </th>
                        ) : null}
                        <th style={{width: '46px'}}>
                            Pos
                            <HelpTooltip id="oe-col-pos" placement="bottom" size={0.7}>
                                Positionsnummer wie in weclapp. Unterpositionen eines Sets tragen die Nummer ihres
                                Kopfes mit angehängter laufender Nummer (4.1, 4.2). Filtern und Sortieren ändert die
                                Nummer nicht.
                            </HelpTooltip>
                        </th>
                        <HeadCell label="Art-Nr" sortKey="art" view={view} onSort={onSort} width="96px"
                                  help="Artikelnummer aus weclapp. Ein Klick auf die Nummer öffnet den Artikel in weclapp."/>
                        <HeadCell label="Bezeichnung" sortKey="name" view={view} onSort={onSort}
                                  help="Artikelbezeichnung. Der Farbbalken davor ist aus dem Namen abgeleitet und dient nur der schnellen Orientierung."/>
                        {showGroup ? (
                            <HeadCell label="Rabattgr." sortKey="group" view={view} onSort={onSort} width="108px"
                                      help="Rabattgruppe des Artikels. Sie entscheidet, welche Staffel für diese Position gilt."/>
                        ) : null}
                        {showStamm ? (
                            <HeadCell label="Liefern ab" sortKey="stamm" view={view} onSort={onSort} width="246px"
                                      help={'Lager, aus dem diese Position bedient wird — aus der Positions-Zuordnung, '
                                          + 'sonst aus dem Artikel-Stammlager, sonst aus der Sortiments-Logik. '
                                          + 'Mit „Keine" wird die Position gar nicht geliefert.'}/>
                        ) : null}
                        <HeadCell label="Verfügbarkeit" sortKey="avail" view={view} onSort={onSort} extraClass="num" width="176px"
                                  help={'Ampel aus dem verfügbaren Bestand des zuständigen Lagers: grün gedeckt, gelb teilweise, rot nicht gedeckt, violett über Produktion.'}
                                  helpDetail={'Verfügbar = Lagerbestand im Lieferlager der Position − von anderen Aufträgen reserviert + von diesem Auftrag schon reserviert. '
                                      + 'Aufträge reservieren Bestand; offene, nicht reservierte Mengen anderer Aufträge werden nicht abgezogen und stehen nur grau darunter. '
                                      + 'Ein Tooltip an der Zahl nennt die reservierenden Lieferscheine und Aufträge.'}/>
                        <HeadCell label="Menge" sortKey="qty" view={view} onSort={onSort} extraClass="num" width="124px"
                                  help={'Bestellte Menge laut Beleg. Mit −/+ oder direkt im Feld ändern; gespeichert wird erst mit „Speichern".'}/>
                        {hasPrice ? (
                            <>
                                <HeadCell label="Einzelpreis €" sortKey="price" view={view} onSort={onSort} extraClass="num" width="116px"
                                          help="Netto-Einzelpreis der Position aus weclapp. Der Preis wird bewusst nicht von Hand gesetzt, er folgt dem Vertriebsweg."/>
                                <th className="num" style={{width: '96px'}}>
                                    Rabatt %
                                    <HelpTooltip id="oe-col-disc" placement="bottom" size={0.7}>
                                        Positionsrabatt in Prozent. Darunter steht, ob der Satz der Staffel dieser
                                        Rabattgruppe entspricht.
                                    </HelpTooltip>
                                </th>
                            </>
                        ) : null}
                        {kommStep ? (
                            <th style={{width: '150px'}}>
                                Geprüft
                                <HelpTooltip id="oe-col-check" placement="bottom" size={0.7}>
                                    Häkchen für die physisch geprüften Positionen — auch für die, bei denen korrekt
                                    nichts da war. Der Stand liegt im Ordermanagement und ist für jeden Bearbeiter
                                    sichtbar.
                                </HelpTooltip>
                            </th>
                        ) : null}
                    </tr>

                    <tr id="oe-theadFilterRow" className="filterrow">
                        {selectable ? <th className="selcell"/> : null}
                        <th>
                            <OeTip tip={'Zeigt nur Positionen, deren Nummer so beginnt — „4" findet auch 4.1 und 4.2.'}>
                                <input
                                    className="colf"
                                    placeholder="Pos"
                                    aria-label="Positionsnummer filtern"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    value={view.posSearch}
                                    onChange={(e) => setView({posSearch: e.target.value.trim()})}
                                />
                            </OeTip>
                        </th>
                        <th>
                            <OeTip tip="Zeigt nur Positionen, deren Artikelnummer diesen Text enthält.">
                                <input
                                    className="colf"
                                    placeholder="Art-Nr"
                                    aria-label="Artikelnummer filtern"
                                    autoComplete="off"
                                    value={view.artSearch}
                                    onChange={(e) => setView({artSearch: e.target.value.trim()})}
                                />
                            </OeTip>
                        </th>
                        <th>
                            <div className="namefilter">
                                <OeTip tip="Freitextsuche über Bezeichnung und Artikelnummer.">
                                    <input
                                        className="colf"
                                        placeholder="🔍 suchen…"
                                        aria-label="Bezeichnung oder Artikelnummer suchen"
                                        autoComplete="off"
                                        value={view.nameSearch}
                                        onChange={(e) => setView({nameSearch: e.target.value.trim()})}
                                    />
                                </OeTip>
                                <OeTip tip="Zeigt nur Artikel, für die eine Masterboxgröße hinterlegt ist.">
                                    <button
                                        type="button"
                                        className={`mbfilt${view.mbFilter ? ' on' : ''}`}
                                        aria-pressed={view.mbFilter}
                                        onClick={() => setView({mbFilter: !view.mbFilter})}
                                    >
                                        📦 MB
                                    </button>
                                </OeTip>
                            </div>
                        </th>
                        {showGroup ? (
                            <th>
                                <OeTip tip="Zeigt nur Positionen einer Rabattgruppe. Angeboten werden die Gruppen, die im Beleg vorkommen.">
                                    <span>
                                        <OeSelect
                                            value={view.groupFilter}
                                            options={groupOptions}
                                            onChange={(v) => setView({groupFilter: v})}
                                            ariaLabel="Rabattgruppe filtern"
                                        />
                                    </span>
                                </OeTip>
                            </th>
                        ) : null}
                        {showStamm ? (
                            <th>
                                <OeTip tip="Zeigt nur Positionen, die aus dem gewählten Lager bedient werden.">
                                    <span>
                                        <OeSelect
                                            value={view.stammFilter}
                                            options={stammOptions}
                                            onChange={(v) => setView({stammFilter: v})}
                                            ariaLabel="Stammlager filtern"
                                        />
                                    </span>
                                </OeTip>
                                {zeigeMasterSchalter && !kommStep ? (
                                    <div className="setall">
                                        <OeTip
                                            tip={filterActive
                                                ? `Stellt die ${visible.length} SICHTBAREN Positionen auf „ab Lahr"; Auftragslager und Filter bleiben. Wirksam mit „Speichern".`
                                                : 'Stellt ALLE Positionen auf „ab Lahr" und merkt Lahr als Auftragslager vor; wirksam erst mit „Speichern".'}
                                            detail={filterActive
                                                ? 'Ohne Filter oder Suche wirkt der Knopf auf alle Positionen und stellt auch das Auftragslager um. Es kommt vorher eine Rückfrage mit der Zahl der betroffenen Positionen.'
                                                : 'Lahr wird zugleich Auftragslager, weil weclapp Entnahmen nur am Auftragslager zulässt. '
                                                    + 'Es kommt vorher eine Rückfrage mit der Zahl der betroffenen Positionen.'}
                                        >
                                            <button
                                                type="button"
                                                className="saseg"
                                                onClick={() => onSetAllStamm('Lahr', filterActive ? visible.map((e) => e.index) : null)}
                                            >
                                                {filterActive ? 'sichtbare Lahr' : 'alle Lahr'}
                                            </button>
                                        </OeTip>
                                        <OeTip
                                            tip={filterActive
                                                ? `Stellt die ${visible.length} SICHTBAREN Positionen auf „ab Schneider (SNV)"; Auftragslager und Filter bleiben. Wirksam mit „Speichern".`
                                                : 'Stellt ALLE Positionen auf „ab Schneider (SNV)" und merkt SNV als Auftragslager vor; wirksam mit „Speichern".'}
                                            detail={filterActive
                                                ? 'Ohne Filter oder Suche wirkt der Knopf auf alle Positionen und stellt auch das Auftragslager um. Es kommt vorher eine Rückfrage mit der Zahl der betroffenen Positionen.'
                                                : 'Es kommt vorher eine Rückfrage mit der Zahl der betroffenen Positionen.'}
                                        >
                                            <button
                                                type="button"
                                                className="saseg"
                                                onClick={() => onSetAllStamm('SNV', filterActive ? visible.map((e) => e.index) : null)}
                                            >
                                                {filterActive ? 'sichtbare Schneider' : 'alle Schneider'}
                                            </button>
                                        </OeTip>
                                        <OeTip tip={'Nimmt nur die Positionen zurück, die per Schieberegler vom Lager des Artikels abweichen. Die Rückfrage listet bis zu acht davon auf; wirksam mit „Speichern".'}>
                                            <button
                                                type="button"
                                                className="saseg reset"
                                                onClick={onResetStamm}
                                            >
                                                ↺ Stammlager
                                            </button>
                                        </OeTip>
                                    </div>
                                ) : null}
                            </th>
                        ) : null}
                        <th>
                            <div className="avfilts">
                                {availChip(2, 'verfügbar')}
                                {availChip(1, 'teilweise verfügbar')}
                                {availChip(0, 'nicht verfügbar')}
                                {availChip(3, 'verfügbar durch Produktion')}
                                {availChip('none', 'nicht liefern', 'Nur Positionen zeigen, die auf „Keine" stehen und nicht geliefert werden. Lässt sich mit den anderen Zuständen kombinieren.')}
                            </div>
                            {kommStep ? (
                                <MassActions
                                    indices={massIndices}
                                    rows={rows}
                                    stock={stock}
                                    order={order}
                                    pendingWarehouse={pendingWarehouse}
                                    lahrGesperrt={lahrGesperrt}
                                    canWarehouse={canWarehouse}
                                    onMass={onMass}
                                />
                            ) : null}
                        </th>
                        <th>{rangeFilter('Menge', 'qtyMin', 'qtyMax', 'die Menge')}</th>
                        {hasPrice ? (
                            <>
                                <th/>
                                <th>{rangeFilter('Rabatt', 'discMin', 'discMax', 'den Rabatt in Prozent')}</th>
                            </>
                        ) : null}
                        {kommStep ? (
                            <th>
                                <CheckAllLahrButton
                                    indices={checkIndices}
                                    rows={rows}
                                    checked={checked}
                                    onCheckAll={onCheckAllLahr}
                                />
                            </th>
                        ) : null}
                    </tr>
                </thead>

                <tbody>
                    {sortedEntries.map((entry) => {
                        const r = entry.row;
                        const i = entry.index;
                        const kitParent = isKitParent(r, kitParents);
                        const kitChild = isKitChild(r);
                        const classes = [];
                        if (r.del) {
                            classes.push('del');
                        } else if (r.locked) {
                            classes.push('locked');
                        } else if (isDirty(r)) {
                            classes.push('dirty');
                        }
                        if (kitChild) {
                            classes.push('kitchild');
                        } else if (kitParent) {
                            classes.push('kitparent');
                        }
                        // Grüner Hintergrund für geprüfte Positionen — rein
                        // optisch und nur im Kommissionierungs-Schritt.
                        if (kommStep && checked.includes(checkKeyOf(r))) {
                            classes.push('stkchecked');
                        }

                        // Felder sperren wie im Original: gelöschte Zeilen,
                        // Set-Inhalte und nicht mehr bearbeitbare Belege.
                        const gesperrt = r.del || r.locked || !editable;
                        const sl = showStamm ? stammLagerOf(r, stock) : '';
                        const stammSrc = r._stammOverride
                            ? 'Positions-Zuordnung am Beleg'
                            : (stock.stammArt[String(r.articleId)] ? 'Artikelfeld in weclapp' : 'Sortiments-Logik');
                        // Die Lagerwahl ist auch am Set-Inhalt erlaubt: Sie ist
                        // dort die einzige speicherbare Änderung und trägt den
                        // Set-Split über zwei Läger.
                        // „Liefern ab" ist nur im Bestands-Schritt editierbar. In
                        // Schritt 6 steht dort ein Kennzeichen: die Schneider-
                        // Lieferscheine sind erzeugt, Positionen dürfen nicht mehr
                        // von SNV auf Lahr wandern (Original `stammEditable`, 4979).
                        const sliderEditable = editable && !r.del && !kitParent && (mode !== 'order' || step === 1);
                        const entfernbar = editable && !r.locked && !kitParent && !kitChild && !r._new;

                        return (
                            <tr key={`${r.id ?? r._key ?? 'n'}-${i}`} className={classes.join(' ') || undefined}>
                                {selectable ? (
                                    <td className="selcell">
                                        {canSelectRow(r) ? (
                                            <button
                                                type="button"
                                                role="checkbox"
                                                aria-checked={sel.has(rowKeyOf(r, i))}
                                                aria-label={`Position ${entry.pos || (i + 1)} auswählen`}
                                                className={`selbox${sel.has(rowKeyOf(r, i)) ? ' on' : ''}`}
                                                onClick={(e) => toggleSel(entry, e.shiftKey)}
                                            >
                                                {sel.has(rowKeyOf(r, i)) ? '✓' : ''}
                                            </button>
                                        ) : null}
                                    </td>
                                ) : null}
                                <td className={`poscell${kitChild ? ' subpos' : ''}`}>{entry.pos || (i + 1)}</td>
                                <td>
                                    {r.articleNumber
                                        ? (order.webBase && r.articleId
                                            ? (
                                                <OeTip tip="Öffnet den Artikel in weclapp in einem neuen Browser-Tab.">
                                                    <a
                                                        className="artlink"
                                                        href={`${order.webBase}/app/article/${encodeURIComponent(r.articleId)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {r.articleNumber}
                                                    </a>
                                                </OeTip>
                                            )
                                            : r.articleNumber)
                                        : <span className="muted">neu</span>}
                                </td>
                                <td className="namecell">
                                    <Swatch title={r.title}/>
                                    {r.title || ''}
                                    {kitParent ? (
                                        <OeTip tip="Set-Artikel — Menge und Bestand hängen an den Unterpositionen.">
                                            <span className="kitbadge">Set</span>
                                        </OeTip>
                                    ) : null}
                                    {r.locked ? <span className="muted"> (Set-Inhalt)</span> : null}
                                    {r._new && editable ? (
                                        <OeTip tip="Entfernt die neu hinzugefügte Position wieder. Sie war noch nicht in weclapp.">
                                            <button type="button" className="link" onClick={() => dispatch({type: 'remove-row', index: i})}>
                                                ✕
                                            </button>
                                        </OeTip>
                                    ) : null}
                                    {entfernbar ? (
                                        <OeTip tip={r.del
                                            ? `Nimmt die Löschmarkierung zurück — die Position bleibt ${mode === 'shipment' ? 'im Lieferschein' : 'im Auftrag'}.`
                                            : 'Markiert die Position zum Löschen. Sie wird durchgestrichen und erst mit „Speichern" aus dem '
                                                + `weclapp-${mode === 'shipment' ? 'Lieferschein' : 'Auftrag'} entfernt.`}>
                                            <button
                                                type="button"
                                                className="link"
                                                aria-label={r.del ? 'Löschmarkierung zurücknehmen' : 'Position zum Löschen markieren'}
                                                onClick={() => dispatch({type: 'toggle-del', index: i})}
                                            >
                                                {r.del ? '↩' : '🗑'}
                                            </button>
                                        </OeTip>
                                    ) : null}
                                    <MasterboxBadge row={r} stock={stock} mode={mode}/>
                                </td>
                                {showGroup ? (
                                    <td className="grpcell">
                                        {groupLabelOf(r, stock) || <span className="muted">–</span>}
                                    </td>
                                ) : null}
                                {showStamm ? (
                                    <td className="stammcell">
                                        {kitParent ? (
                                            <OeTip tip="Der Set-Kopf folgt automatisch seinen Unterpositionen — je nachdem, welche ab Lahr bzw. Schneider geliefert werden.">
                                                <span className="muted" style={{fontSize: 11}}>folgt Inhalt</span>
                                            </OeTip>
                                        ) : sliderEditable ? (
                                            <>
                                                <WarehouseSlider
                                                    row={r}
                                                    index={i}
                                                    current={sl}
                                                    stock={stock}
                                                    editable
                                                    onChange={(index, value) => dispatch({type: 'set-stamm', index, value})}
                                                    orderRef={orderRef}
                                                />
                                                {r._stammOverride ? (
                                                    <OeTip tip="Diese Position weicht vom Lager des Artikels ab — sie wurde hier von Hand zugeordnet.">
                                                        <span className="slov"> •</span>
                                                    </OeTip>
                                                ) : null}
                                            </>
                                        ) : (
                                            <>
                                                <OeTip tip={sl === STAMM_NONE
                                                    ? 'Diese Position wird nicht geliefert.'
                                                    : `Stammlager laut ${stammSrc}`}>
                                                    <span className={`slchip sl-${String(sl).toLowerCase()}`}>
                                                        {STAMM_LABELS[sl] || sl}
                                                    </span>
                                                </OeTip>
                                                {isStockInPflege(r, stock, order) ? <PflegeMarker/> : null}
                                            </>
                                        )}
                                    </td>
                                ) : null}
                                <td className="availcell">
                                    <span className="av-slot"><AvailIcon cat={filterCat(r, stock, order)}/></span>
                                    <span className="av-actions">
                                        {kommStep ? (
                                            <StockActionButtons
                                                row={r}
                                                index={i}
                                                stock={stock}
                                                order={order}
                                                kitParent={kitParent}
                                                lahrGesperrt={lahrGesperrt}
                                                canWarehouse={canWarehouse}
                                                onBook={onBook}
                                                onUnpick={onUnpick}
                                                onPosOut={onPosOut}
                                            />
                                        ) : (
                                            <PurchaseInfo row={r} stock={stock} order={order}/>
                                        )}
                                    </span>
                                    {kitParent || mode !== 'order' || isExcluded(r) ? null : (
                                        <AutoReserveChip
                                            row={r}
                                            position={autoPositionen[String(r.id)] || null}
                                            laeuft={autoLaeuft}
                                        />
                                    )}
                                    {kitParent ? null : <StockFigures row={r} stock={stock} order={order} orderRef={orderRef}/>}
                                </td>
                                <td className="num">
                                    <span className="qtywrap">
                                        <OeTip tip={'Menge um 1 senken. Gespeichert wird erst mit „Speichern".'}>
                                            <button
                                                type="button"
                                                disabled={gesperrt}
                                                aria-label="Menge um eins senken"
                                                onClick={() => dispatch({type: 'step-qty', index: i, step: -1})}
                                            >
                                                −
                                            </button>
                                        </OeTip>
                                        <input
                                            className="q num"
                                            value={r.quantity}
                                            disabled={gesperrt}
                                            inputMode="decimal"
                                            autoComplete="off"
                                            aria-label="Menge"
                                            onChange={(e) => dispatch({type: 'set-field', index: i, field: 'quantity', value: e.target.value})}
                                        />
                                        <OeTip tip={'Menge um 1 erhöhen. Gespeichert wird erst mit „Speichern".'}>
                                            <button
                                                type="button"
                                                disabled={gesperrt}
                                                aria-label="Menge um eins erhöhen"
                                                onClick={() => dispatch({type: 'step-qty', index: i, step: 1})}
                                            >
                                                +
                                            </button>
                                        </OeTip>
                                    </span>
                                </td>
                                {hasPrice ? (
                                    <>
                                        <td className="num">
                                            {r._new && !r._shipCost ? <PreviewPrice row={r}/> : fmt(r.unitPrice)}
                                        </td>
                                        <td className="num disccell">
                                            <input
                                                className="d num"
                                                value={r.discountPercentage}
                                                disabled={gesperrt}
                                                inputMode="decimal"
                                                autoComplete="off"
                                                aria-label="Rabatt in Prozent"
                                                onChange={(e) => dispatch({type: 'set-field', index: i, field: 'discountPercentage', value: e.target.value})}
                                            />
                                            <StaffelHint row={r} stock={stock} mode={mode} planByGroup={planByGroup}/>
                                        </td>
                                    </>
                                ) : null}
                                {kommStep ? (
                                    <td className="chkcell">
                                        <CheckButton
                                            row={r}
                                            index={i}
                                            stock={stock}
                                            order={order}
                                            checked={checked}
                                            onToggle={onToggleCheck}
                                        />
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Unten klebend, damit sie beim Scrollen durch lange Belege
                sichtbar bleibt, ohne die Spaltenköpfe zu verdecken. */}
            {selectable ? (
                <BulkBar
                    count={selEntries.length}
                    canStamm={step === 1}
                    onApply={applyBulk}
                    onClear={() => setSel(new Set())}
                />
            ) : null}

            <AddPositionRow
                mode={mode}
                canAdd={order.canAdd}
                editable={editable}
                status={addStatus}
                onStatus={setAddStatus}
                onAdd={(article, qty) => {
                    setAddStatus({tone: 'ok', text: `${article.articleNumber} hinzugefügt`});
                    onAddPosition(article, qty);
                }}
            />

            <div id="oe-foot" style={{height: 70}}/>
        </>
    );
}
